import { useEffect, useRef, useContext } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "./context/AuthContext";

// Same values that used to sit as a static <script> tag in public/index.html
const WIDGET_SCRIPT_ID = "lmsbots-public-chatbot-widget-script";
const WIDGET_SRC = "https://apilms.deetyasoft.com/api/chatbot/public/widget.js";
const WIDGET_KEY = "wgt_0NYU8dzuUnE8ZchSQHzcDYaJ";
const WIDGET_API_BASE = "https://apilms.deetyasoft.com";

// Only these public marketing-website routes should ever show the chatbot.
// Everything else — login/register/reset-password/oauth-callback and the
// entire authenticated dashboard (SalesGod, Incoming Mode, Leads, etc.) —
// must never show it.
const PUBLIC_ROUTES = [
  "/",
  "/about-us",
  "/public-page",
  "/contact",
  "/pricing",
  "/features",
  "/privacy-policy",
  "/terms-and-conditions",
  "/solutions/healthcare",
  "/solutions/education",
  "/solutions/real-estate",
  "/solutions/insurance",
  "/solutions/banking-finance",
  "/solutions/technology",
];

/**
 * Mounted once near the root of the app (see App.js). Renders nothing itself
 * — it only injects/removes the external chatbot <script> based on the
 * current route + login state.
 */
const PublicChatbotWidget = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const location = useLocation();

  // Tracks every DOM node the widget script itself injects (its launcher
  // button, iframe, stylesheet, etc.) into <head>/<body>, so it can all be
  // cleanly removed without needing to know the widget's internal markup.
  const injectedNodesRef = useRef([]);
  const observerRef = useRef(null);
  const isInjectedRef = useRef(false);

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
  const shouldShowWidget = !isAuthenticated && isPublicRoute;

  useEffect(() => {
    const removeWidget = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      injectedNodesRef.current.forEach((node) => {
        if (node && node.parentNode) {
          node.parentNode.removeChild(node);
        }
      });
      injectedNodesRef.current = [];
      isInjectedRef.current = false;
    };

    const injectWidget = () => {
      if (isInjectedRef.current) return;
      isInjectedRef.current = true;

      // Watch <head> and <body> for anything the widget script adds, so we
      // can fully tear it down later (button, popup panel, styles, etc.).
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            injectedNodesRef.current.push(node);
          });
        });
      });
      observer.observe(document.head, { childList: true });
      observer.observe(document.body, { childList: true });
      observerRef.current = observer;

      const script = document.createElement("script");
      script.id = WIDGET_SCRIPT_ID;
      script.src = WIDGET_SRC;
      script.setAttribute("data-widget-key", WIDGET_KEY);
      script.setAttribute("data-api-base", WIDGET_API_BASE);
      script.defer = true;

      document.body.appendChild(script);
      injectedNodesRef.current.push(script);
    };

    if (shouldShowWidget) {
      injectWidget();
    } else {
      removeWidget();
    }

    return () => {
      // Also clean up if the component itself ever unmounts.
      removeWidget();
    };
  }, [shouldShowWidget]);

  return null;
};

export default PublicChatbotWidget;
